import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import { Loader, Notification } from "./components/Alerts";
import { AuthLayout, PublicLayout } from "./components/Layouts";
import SessionRedirectBridge from "./components/SessionRedirectBridge";
import useUserStore from "./context/useUserStore";
import {
  AdminDashboard,
  AppRedirect,
  AppShare,
  Book,
  Categories,
  ChangePassword,
  Donate,
  EventAdmin,
  EventDetail,
  Events,
  ForgotPassword,
  Give,
  Home,
  Login,
  Menu,
  MenuAdmin,
  MenuItem,
  NotFound404,
  Partners,
  PrivacyPolicy,
  Profile,
  ResetPassword,
  Users,
  VehicleAdmin,
} from "./pages";
/**
 * AuthGuard — Redirects unauthenticated users to /login.
 * If user must change password, redirects all admin routes to /change-password.
 */
function RequireAuth({ children }) {
  const currentUser = useUserStore((state) => state.currentUser);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return children;
}

/**
 * App — Root component: dual-layout routing per BLU-004 §4.2.
 * Public routes use PublicLayout (customer-facing nav).
 * Admin routes use AuthLayout (sidebar dashboard), guarded by RequireAuth.
 * Auth routes use minimal layout (no nav).
 */
function App() {
  // Auth state is hydrated synchronously in useUserStore's initial state
  // (DEF-007), so no post-render hydration effect is needed here.
  return (
    <div className="w-full min-h-screen bg-cream overflow-x-hidden">
      <Notification />
      <Loader />
      <SessionRedirectBridge />

      <Routes>
        {/* ── Public routes — always accessible ── */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/menu/:id" element={<MenuItem />} />
          {/* SPR-014: online ordering/checkout disabled at launch — cart,
              checkout, and order-confirmation routes removed. The cart/checkout
              + payment flow returns post-launch via SPR-013. */}
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/book" element={<Book />} />
          <Route path="/give" element={<Give />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Route>

        {/* ── Admin routes — require authentication ── */}
        <Route
          element={
            <RequireAuth>
              <AuthLayout />
            </RequireAuth>
          }
        >
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/app-share" element={<AppShare />} />
          <Route path="/admin/menu" element={<MenuAdmin />} />
          <Route path="/admin/categories" element={<Categories />} />
          <Route path="/admin/vehicles" element={<VehicleAdmin />} />
          <Route path="/admin/events" element={<EventAdmin />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/profile" element={<Profile />} />
        </Route>

        {/* ── Auth routes — minimal layout (no nav) ── */}
        <Route path="/login" element={<Login />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/* Shared token-based set-password page (forgot reset + invite accept) */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<ResetPassword />} />

        {/* ── Hidden store-redirect (SPR-021 A-02) — not in any nav/footer.
            Standalone (no layout); shared via the admin QR code. ── */}
        <Route path="/app" element={<AppRedirect />} />

        {/* ── 404 ── */}
        <Route path="*" element={<NotFound404 />} />
      </Routes>
    </div>
  );
}

export default App;
